import {
  Form,
  RadioGroupField,
  SubmitButton,
  TextInputField,
} from '@/components';
import {
  AbilityLabelModifier,
  PermissionTypeLabelModifier,
  RoleFormSchema,
  permissionType,
} from '@/features/tenant-manager/role/_models/role';
import { useForm } from '@/hooks';
import { booleanStringType } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof RoleFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const RoleForm: FC<Props> = ({ initialValues, onSubmit }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      resourceGroupPermissions: {
        project: 'none',
        indicator: 'none',
        globalTag: 'none',
        localTag: 'none',
        masterTable: 'none',
        administrator: 'none',
      },
      continueAbility: false,
      templateAbility: false,
      lockAbility: false,
      publishAbility: false,
      ...initialValues,
    },
    resolver: zodResolver(RoleFormSchema),
  });

  const abilityItems = booleanStringType.options
    .map((key) => ({
      value: key === 'true',
      label: AbilityLabelModifier[key],
    }))
    .reverse();

  const resourceGroupItems = permissionType.options.map((key) => ({
    value: key,
    label: PermissionTypeLabelModifier[key],
  }));

  const getResourceGroupItems = (omit?: z.infer<typeof permissionType>[]) => {
    return !omit
      ? resourceGroupItems
      : resourceGroupItems.filter((item) => !omit.includes(item.value));
  };

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <TextInputField
        name="name"
        autocompleteKey="role.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />

      {[
        {
          name: ['resourceGroupPermissions', 'project'],
          label: t('project-management'),
          items: getResourceGroupItems(),
          onChange: (e) => {
            const value = e.target.value;
            setValue('resourceGroupPermissions.project', value, {
              shouldDirty: true,
            });
          },
        },
        {
          name: ['resourceGroupPermissions', 'indicator'],
          label: t('indicator-management'),
          items: getResourceGroupItems(['privilege', 'view-all-privilege']),
        },
        {
          name: ['resourceGroupPermissions', 'globalTag'],
          label: t('scope-tag-management', { scope: t('global-tag') }),
          items: getResourceGroupItems(['privilege', 'view-all-privilege']),
        },
        {
          name: ['resourceGroupPermissions', 'localTag'],
          label: t('scope-tag-management', { scope: t('local-tag') }),
          items: getResourceGroupItems([
            'view',
            'privilege',
            'view-all-privilege',
          ]),
        },
        {
          name: ['resourceGroupPermissions', 'masterTable'],
          label: t('master-table-management'),
          items: getResourceGroupItems(['privilege', 'view-all-privilege']),
        },
        {
          name: ['resourceGroupPermissions', 'administrator'],
          label: t('administrator'),
          items: getResourceGroupItems(['privilege', 'view-all-privilege']),
        },
        {
          name: 'continueAbility',
          label: t('continue-ability'),
          items: abilityItems,
        },
        {
          name: 'lockAbility',
          label: t('lock-project'),
          items: abilityItems,
        },
        {
          name: 'templateAbility',
          label: t('template-management'),
          items: abilityItems,
        },
        {
          name: 'publishAbility',
          label: t('publish-ability'),
          items: abilityItems,
        },
      ].map((d) => {
        const name = Array.isArray(d.name) ? d.name.join('.') : d.name;
        return (
          <RadioGroupField
            key={name}
            name={name as keyof FormValues}
            label={d.label}
            control={control}
            options={d.items}
            errors={errors}
            required
            onChange={d.onChange}
          />
        );
      })}

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
